import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  claimAppConnectorProbes,
  listAppConnectorProfiles,
  releaseAppConnectorProbes,
} from '../apps/api';

type Profile = { data_id: string; name?: string | null; device_id: string; claimed?: boolean };

type Props = {
  appId: string;
  tenantId?: string | null;
  /** Reload the app's bindings after a claim/release (the iot_telemetry filter changes). */
  onChanged?: () => void | Promise<void>;
};

/**
 * AquaCheck probes for one app instance. Claiming gives the app its own sync
 * of those probes, registers them as the app's devices and adds them to its
 * iot_telemetry filter. Same backend as the dc__connector_claim MCP tool.
 */
export default function ConnectorProbesCard({ appId, tenantId, onChanged }: Props) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'warning'; text: string } | null>(null);
  const [notInstalled, setNotInstalled] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotInstalled(false);
      const res = await listAppConnectorProfiles(appId, tenantId);
      setProfiles(res.profiles);
      setSelected(new Set());
    } catch (err: any) {
      setProfiles([]);
      const message = String(err?.message || '');
      if (message === 'connector_not_installed') {
        setNotInstalled(true);
      } else if (message === 'connector_credentials_missing') {
        setError('AquaCheck is installed but has no API key yet. Add it in API Manager > AquaCheck > Credentials.');
      } else {
        setError(message || 'Failed to load AquaCheck probes');
      }
    } finally {
      setLoading(false);
    }
  }, [appId, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (dataId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(dataId)) next.delete(dataId);
      else next.add(dataId);
      return next;
    });
  };

  const chosen = profiles.filter((p) => selected.has(p.data_id));
  const toClaim = chosen.filter((p) => !p.claimed).map((p) => p.data_id);
  const toRelease = chosen.filter((p) => p.claimed).map((p) => p.data_id);

  const run = async (action: 'claim' | 'release') => {
    try {
      setBusy(true);
      setError(null);
      setNotice(null);
      if (action === 'claim') {
        const res = await claimAppConnectorProbes(appId, tenantId, { data_ids: toClaim });
        const failed = (res?.devices || []).filter((d: any) => !d.ok).map((d: any) => d.device_id);
        const bindingWarning = res?.telemetry_binding?.warning;
        if (failed.length || bindingWarning) {
          setNotice({
            severity: 'warning',
            text: [
              failed.length ? `Claimed, but these devices could not be registered for this app: ${failed.join(', ')}.` : null,
              bindingWarning || null,
            ].filter(Boolean).join(' '),
          });
        } else {
          setNotice({ severity: 'success', text: `Claimed ${toClaim.length} probe(s). Sync runs every ${Math.round((res?.every_seconds || 1800) / 60)} min.` });
        }
      } else {
        await releaseAppConnectorProbes(appId, tenantId, { data_ids: toRelease });
        setNotice({ severity: 'success', text: `Released ${toRelease.length} probe(s) from this app.` });
      }
      await load();
      await onChanged?.();
    } catch (err: any) {
      setError(err?.message || `Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">AquaCheck probes</Typography>
            <Typography variant="body2" color="text.secondary">
              Soil moisture probes this app reads. Each probe belongs to one app. A farm&apos;s probe appears here once its owner
              enables API Access for FarmSpace in AquaCheckWEB.
            </Typography>
          </Box>
          <Button size="small" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading || busy}>
            Refresh
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}

        {loading ? (
          <CircularProgress size={20} />
        ) : notInstalled ? (
          <Alert severity="info" variant="outlined">
            AquaCheck is not installed for this tenant. A platform operator installs it once in API Manager &gt; Connectors and adds the API key.
          </Alert>
        ) : profiles.length === 0 ? (
          !error && <Alert severity="info" variant="outlined">No AquaCheck probes available to this app.</Alert>
        ) : (
          <Stack spacing={0.5}>
            {profiles.map((p) => (
              <Stack key={p.data_id} direction="row" alignItems="center" spacing={1}>
                <FormControlLabel
                  sx={{ flex: 1, mr: 0 }}
                  control={<Checkbox size="small" checked={selected.has(p.data_id)} onChange={() => toggle(p.data_id)} />}
                  label={(
                    <Typography variant="body2">
                      {p.name || p.data_id} <Typography component="span" variant="caption" color="text.secondary">({p.device_id})</Typography>
                    </Typography>
                  )}
                />
                {p.claimed && <Chip size="small" color="success" label="This app" />}
              </Stack>
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" variant="contained" disabled={busy || toClaim.length === 0} onClick={() => run('claim')}>
            {busy ? 'Working…' : `Claim selected (${toClaim.length})`}
          </Button>
          <Button size="small" variant="outlined" color="error" disabled={busy || toRelease.length === 0} onClick={() => run('release')}>
            Release selected ({toRelease.length})
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
